import React, { useCallback, useEffect, useState } from "react";
import EditorAddIcon from "@atlaskit/icon/glyph/editor/add";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import DragHandlerIcon from "@atlaskit/icon/glyph/drag-handler";
import compose from "ramda/src/compose";
import pathOr from "ramda/src/pathOr";
import mergeRight from "ramda/src/mergeRight";
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
} from "react-sortable-hoc";
import { Modal } from "../Modal";
import { Button } from "../../UI/Button";
import { useModalContext } from "../../contexts/ModalContext/context";
import { Grid } from "../../UI/Grid";
import { getControllerTextFieldRender } from "../../helpers/getControllerTextFieldRender";
import { getControllerTextAreaRender } from "../../helpers/getControllerTextAreaRender";
import { pathOrString } from "../../helpers/pathOrString";
import { normalize } from "../../helpers/normalize";
import { IAnswer } from "../../constants/types";
import { Box } from "../../UI/Box";
import { Typography } from "../../UI/Typography";
import { COLOR_STALE_GRAY } from "../../constants/styles";
import { QUESTIONS_ANSWER_CHAR_LIMIT } from "../../constants/general";
import { useSnackbar } from "../../hooks/useSnackbar";
import { TagSelect } from "../TagSelect";
import { RetainAnswerSelect } from "../RetainAnswerSelect";
import { getTag } from "../../helpers/getTag";

export interface IProps {
  data: any;
  onHide: any;
  onSave: any;
  showTagColumn?: any;
}
interface RetainAnswer {
  value: boolean,
  label: string,
}

interface IFormData {
  questionText: string;
  disclaimer: string;
  answers: IAnswer[];
  tag?: {
    value: number;
    label: string;
  };
  retainAnswer: boolean | null | RetainAnswer;
}

const TITLES = {
  edit: "Modify Select Question",
  new: "Create Select Question",
};

const INITIAL_ANSWERS = [
  {
    answerOrder: 0,
    answerText: "",
  },
  {
    answerOrder: 1,
    answerText: "",
  },
];

const HELPER_CONTAINER = () =>
  document.getElementById("container") as HTMLElement;

const Handle = SortableHandle(({ className }: any) => (
  <Box
    width={32}
    height={32}
    display="flex"
    alignItems="center"
    justifyContent="center"
    className={className}
  >
    <DragHandlerIcon label="Drag icon" size="large" />
  </Box>
));

const SortableItem = SortableElement(
  ({ item, idx, onRemove, control }: any) => (
    <Grid container spacing={1}>
      <Grid item>
        <Handle />
      </Grid>
      <Grid item xs>
        <Controller
          control={control}
          name={`answers[${idx}].answerText`}
          render={getControllerTextFieldRender({
            maxLength: QUESTIONS_ANSWER_CHAR_LIMIT,
            required: true,
          })}
        />
      </Grid>
      <Grid item>
        <Button
          onClick={() => onRemove(idx)}
          isDisabled={!onRemove}
          appearance="danger"
        >
          Delete
        </Button>
      </Grid>
    </Grid>
  )
);

const SortableList = SortableContainer(
  ({
    items,
    onItemRemove,
    control,
  }: {
    items: IAnswer[];
    onItemRemove: any;
    control: any;
  }) => (
    <Grid container spacing={1}>
      {items.map((item, index) => (
        <Grid item xs={12} key={item.id}>
          <SortableItem
            control={control}
            item={item}
            idx={index}
            index={index}
            onRemove={items.length > 2 && onItemRemove}
          />
        </Grid>
      ))}
    </Grid>
  )
);

const getTagObj = (tag: any) =>
  tag?.value
    ? {
        tag: {
          id: tag.value,
          name: tag.label,
        },
      }
    : { tag: null, clearTag: true };

export const SelectQuestionModal: React.FC<IProps> = ({
  data,
  onHide,
  onSave,
  showTagColumn,
}) => {
  const { showErrorSnackbar } = useSnackbar();
  const [isLoading, setLoading] = useState(false);
  const { setIsOpen } = useModalContext();
  const {
    reset,
    formState: { errors },
    control,
    handleSubmit,
  } = useForm<IFormData>();
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "answers",
  });

  const isEdit = typeof data === "object";
  const visible = isEdit || data === true;

  React.useEffect(() => {
    setIsOpen(visible);
  }, [visible]);

  const handleUpdate = async (newData: IFormData) => {
    setLoading(true);

    const getRetainAnswerValue = () => {
      if (newData.retainAnswer !== null && newData.retainAnswer !== undefined) {
        // Handle both boolean and object formats
        if (typeof newData.retainAnswer === 'boolean') {
          return newData.retainAnswer;
        }
        const retainAnswer = newData.retainAnswer as RetainAnswer;
        return retainAnswer.value;
      }
      return true;
    };

    const { success, error } = await onSave({
      ...(isEdit ? data : {}),
      ...getTagObj(newData.tag),
      questionType: "SELECT",
      questionText: newData.questionText,
      answers: newData.answers.map((asw, idx) => ({
        ...asw,
        answerOrder: idx,
      })),
      disclaimer: newData.disclaimer,
      disclaimerText: newData.disclaimer,
      retainAnswer: getRetainAnswerValue(),
    });

    if (success) {
      onHide();
    } else {
      showErrorSnackbar(error);
    }

    setLoading(false);
  };

  const onSortEnd = useCallback(
    ({ newIndex, oldIndex }) => {
      if (newIndex !== oldIndex) {
        move(oldIndex, newIndex);
      }
    },
    [normalize(fields)]
  );

  const onAnswerAdd = useCallback(() => {
    append({
      answerOrder: fields.length + 1,
      answerText: "",
    } as IAnswer);
  }, [normalize(fields)]);

  const getDefaultInputProps = mergeRight({ errors });

  const getTextControllerRender = React.useCallback(
    // @ts-ignore
    compose(getControllerTextFieldRender, getDefaultInputProps),
    []
  );

  const getTextAreaControllerRender = React.useCallback(
    // @ts-ignore
    compose(getControllerTextAreaRender, getDefaultInputProps),
    []
  );

  useEffect(() => {
    reset({
      questionText: pathOrString(["questionText"], data),
      disclaimer: pathOrString(["disclaimer"], data),
      answers: pathOr(INITIAL_ANSWERS, ["answers"], data),
      tag: getTag(data) as any,
      retainAnswer: pathOr(true, ["retainAnswer"], data),
    });
  }, [normalize(data), isEdit]);

  return (
    <Modal
      maxWidth="md"
      onClose={onHide}
      title={isEdit ? TITLES.edit : TITLES.new}
      containerSx={{
        overflow: "visible",
      }}
      contentSx={{
        overflow: "visible",
      }}
      actions={
        <>
          <Button appearance="subtle" onClick={() => onHide()}>
            Cancel
          </Button>
          <Button
            appearance="primary"
            onClick={handleSubmit(handleUpdate)}
            isLoading={isLoading}
          >
            Save Changes
          </Button>
        </>
      }
    >
      <Grid container spacing={2} px={3} py={2} width={658}>
        <Grid item xs={12}>
          <Controller
            name="questionText"
            control={control}
            // @ts-ignore
            render={getTextControllerRender({
              label: "Question",
              required: true,
            })}
          />
        </Grid>
        <Grid item xs={12} id="container">
          <Typography
            mb={1.5}
            fontSize={12}
            fontWeight={700}
            color={COLOR_STALE_GRAY}
            sx={{ textTransform: "uppercase" }}
          >
            Answers (minimum of 2 answers)
          </Typography>
          <SortableList
            control={control}
            pressDelay={100}
            onSortEnd={onSortEnd}
            items={fields}
            onItemRemove={remove}
            helperContainer={HELPER_CONTAINER}
          />
        </Grid>
        {fields.length < 5 && (
          <Grid item xs={12} display="flex" justifyContent="center">
            <Button
              appearance="primary"
              iconBefore={<EditorAddIcon label="Plus" />}
              onClick={onAnswerAdd}
            >
              Add Another Answer
            </Button>
          </Grid>
        )}
        <Grid item xs={12}>
          <Controller
            name="disclaimer"
            control={control}
            // @ts-ignore
            render={getTextAreaControllerRender({
              label: "Disclaimer (optional)",
              maxLength: 1000,
              minimumRows: 5,
            })}
          />
        </Grid>
        {showTagColumn && (
          <Grid item xs={12}>
            <Controller
              name="tag"
              control={control}
              render={({ field }: any) => (
                <TagSelect {...field} label="Tag (optional)" />
              )}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <Controller
            name="retainAnswer"
            control={control}
            render={({ field }: any) => (
              <RetainAnswerSelect {...field} label="Retain Answer" />
            )}
          />
        </Grid>
      </Grid>
    </Modal>
  );
};
